import React, { useState, useMemo } from "react";

// Normalize name for comparison
const normalizeName = (name) =>
  name.toLowerCase().replace(/\s+/g, " ").trim();

const BulkAddSection = ({ addPlayers, THEME_COLOR, levelColor, existingPlayers = [] }) => {
  const [nameInput, setNameInput] = useState("");
  const [draftLevels, setDraftLevels] = useState({});
  const [defaultLevel, setDefaultLevel] = useState(3);

  const existingNameSet = useMemo(() => {
    return new Set(existingPlayers.map(p => normalizeName(p.name)));
  }, [existingPlayers]);
  // Parse names from the textarea
  const parsedNames = useMemo(() => {
    const counts = {};
    return nameInput
      .split(/[\n,]+/)
      .map((n) => n.trim())
      .filter(Boolean)
      .map((name) => {
        const key = normalizeName(name);
        counts[key] = (counts[key] || 0) + 1;
        return counts[key] === 1 ? name : `${name} (${counts[key]})`;
      });
  }, [nameInput]);

  // Detect duplicate names (case-insensitive)
  const duplicateNames = useMemo(() => {
    const seen = new Set();
    const duplicates = new Set();

    parsedNames.forEach((name) => {
      const normalized = normalizeName(name);
      if (seen.has(normalized)) {
        duplicates.add(normalized);
      }
      seen.add(normalized);
    });

    return duplicates;
  }, [parsedNames]);

  const handleLevelChange = (name, level) => {
    setDraftLevels((prev) => ({ ...prev, [name]: level }));
  };

  const onConfirm = () => {
    if (duplicateNames.size > 0) return;

    const finalPayload = parsedNames.map((name) => ({
      name,
      level: draftLevels[name] || defaultLevel,
    }));

    addPlayers(finalPayload);
    setNameInput("");
    setDraftLevels({});
  };
  const hasExistingConflicts = useMemo(() => {
    return parsedNames.some((name) =>
      existingNameSet.has(
        normalizeName(name.replace(/\s\(\d+\)$/, ""))
      )
    );
  }, [parsedNames, existingNameSet]);
  return (
    <div className="p-3 rounded-4 mb-3 border shadow-sm">
      <label className="small fw-bold text-muted text-uppercase mb-2 d-block">
        Bulk Add Players
      </label>

      <textarea
        className="form-control border-0 shadow-sm rounded-3 mb-2"
        rows={3}
        placeholder="Enter names (one per line)..."
        value={nameInput}
        onChange={(e) => setNameInput(e.target.value)}
      />

      {duplicateNames.size > 0 && (
        <div className="small text-danger fw-bold mb-2">
          Duplicate names detected. Please fix them.
        </div>
      )}
      {hasExistingConflicts && (
        <div className="small text-warning fw-bold mb-2">
          Some names already exist in the player list.
        </div>
      )}
      {parsedNames.length > 0 && (
        <div
          className="d-flex flex-wrap gap-2 mb-3 p-2 rounded-3"
          style={{ border: "2px dashed #dee2e6" }}
        >
          {parsedNames.map((name) => {
            const currentLvl = draftLevels[name] || defaultLevel;
            const isDuplicate = duplicateNames.has(normalizeName(name));
            const normalized = normalizeName(name.replace(/\s\(\d+\)$/, ""));
            const alreadyExists = existingNameSet.has(normalized);
            return (
              <div
                key={name}
                className={`d-flex align-items-center gap-1 p-1 pe-2 rounded-pill border shadow-sm ${alreadyExists ? "bg-warning-subtle border-warning" : ""
                  }`}
                style={{ borderLeft: `4px solid ${levelColor(currentLvl)}` }}
              >
                <span
                  className={`ps-2 small fw-bold text-truncate ${alreadyExists ? "text-warning" : ""
                    }`}
                  style={{ maxWidth: "100px" }}
                >
                  {name}
                </span>

                <select
                  className="form-select form-select-sm border-0 bg-transparent p-0 px-1 fw-bold text-primary"
                  style={{ width: "45px", fontSize: "0.75rem" }}
                  value={currentLvl}
                  onChange={(e) =>
                    handleLevelChange(name, Number(e.target.value))
                  }
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center pt-2 border-top">
        <div className="d-flex align-items-center gap-2">
  <span
    className="small"
    style={{ color: "var(--text-muted)" }}
  >
    Default:
  </span>

  <select
    className="form-select form-select-sm fw-bold w-auto"
    value={defaultLevel}
    onChange={(e) => setDefaultLevel(Number(e.target.value))}
    style={{
      background: "var(--card-bg)",
      color: "var(--text-color)",
      border: "1px solid var(--border-color)"
    }}
  >
    {[1, 2, 3, 4, 5, 6, 7].map((l) => (
      <option key={l} value={l}>
        Lvl {l}
      </option>
    ))}
  </select>
</div>

        <button
          disabled={parsedNames.length === 0 || hasExistingConflicts}
          onClick={onConfirm}
          className="btn btn-sm px-4 rounded-pill shadow-sm fw-bold text-white"
          style={{ backgroundColor: THEME_COLOR }}
        >
          Add {parsedNames.length} Players
        </button>

      </div>
    </div>
  );
};

export default BulkAddSection;
